import { View, Text, ActivityIndicator } from 'react-native';
import { useState, useEffect, useRef } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { setPendingAuthRedirect } from '../../lib/auth-redirect';
import { trpc, queryClient } from '../../lib/trpc';
import { supabase } from '../../lib/supabase';
import { Button, Card, WebHead } from '../../components';

export default function InviteAcceptPage() {
  const router = useRouter();
  const { autoAccept, code } = useLocalSearchParams<{ autoAccept?: string; code: string }>();
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const autoAcceptStartedRef = useRef(false);

  const validateQ = trpc.invites.validate.useQuery(
    { code: code ?? '' },
    { enabled: !!code },
  );

  const acceptMutation = trpc.invites.accept.useMutation();

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setIsAuthenticated(!!data.session);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setIsAuthenticated(!!session);
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (autoAccept !== '1') return;
    if (!code || !validateQ.data?.valid) return;
    if (isAuthenticated !== true) return;
    if (accepting || autoAcceptStartedRef.current) return;

    autoAcceptStartedRef.current = true;
    void handleAccept();
  }, [accepting, autoAccept, code, isAuthenticated, validateQ.data?.valid]);

  const handleAccept = async () => {
    if (!code) return;
    if (isAuthenticated !== true) {
      const redirect = `/invite?code=${encodeURIComponent(code)}&autoAccept=1`;
      await setPendingAuthRedirect(redirect);
      router.replace(`/(auth)/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }

    setError(null);
    setAccepting(true);
    try {
      await acceptMutation.mutateAsync({ code });
      // The signed-in user was auto-provisioned into a fresh household before
      // accepting the invite. Clear that cached state so the app shell fetches
      // the inviter's household instead of briefly routing off stale step 0 data.
      queryClient.clear();

      // Redirect to dashboard — they're now in the inviter's household.
      router.replace('/(app)');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  const handleSignInFirst = () => {
    const redirect = `/invite?code=${encodeURIComponent(code ?? '')}&autoAccept=1`;
    void setPendingAuthRedirect(redirect);
    router.push(`/(auth)/login?redirect=${encodeURIComponent(redirect)}`);
  };

  if (!code) {
    return (
      <View className="flex-1 bg-gray-50 dark:bg-gray-900 justify-center items-center p-6">
        <Text className="text-lg text-gray-800 dark:text-gray-200">Invalid invite link</Text>
      </View>
    );
  }

  return (
    <>
      <WebHead><title>Accept Invite - Per Diem</title></WebHead>
      <View className="flex-1 bg-gray-50 dark:bg-gray-900 justify-center items-center p-6">
        <View className="w-full max-w-sm">
          {validateQ.isLoading ? (
            <ActivityIndicator size="large" color="#2B7A50" />
          ) : !validateQ.data?.valid ? (
            <Card>
              <Text className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Invite expired or invalid
              </Text>
              <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                This invite link is no longer valid. Ask the person who invited you to send a new one.
              </Text>
              <Button onPress={() => router.replace('/(auth)/login')}>
                Go to Login
              </Button>
            </Card>
          ) : (
            <Card>
              <Text className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Join household
              </Text>
              <Text className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {validateQ.data.inviterName
                  ? `${validateQ.data.inviterName} invited you to join their household on Per Diem.`
                  : 'You\'ve been invited to join a household on Per Diem.'}
              </Text>
              <Text className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                You'll share access to accounts, budgets, and a FIRE plan that reflects both of your financial pictures.
              </Text>

              {error && (
                <View className="rounded-xl bg-red-50 dark:bg-red-900/30 px-4 py-3 mb-4">
                  <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>
                </View>
              )}

              {isAuthenticated === null ? (
                <Button disabled loading>
                  Checking your session…
                </Button>
              ) : isAuthenticated === false ? (
                <Button onPress={handleSignInFirst}>
                  Sign in to accept
                </Button>
              ) : (
                <Button onPress={handleAccept} loading={accepting}>
                  Accept Invite
                </Button>
              )}
            </Card>
          )}
        </View>
      </View>
    </>
  );
}
